"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { MouseEvent, ReactNode } from "react";
import { Check, Copy } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type CopyState = "idle" | "copied" | "error";

const SKIP_SELECTOR =
  "[data-llm-copy-control], [data-llm-copy-skip], [data-section-level], .adsbygoogle, [data-ad-client], script, style, noscript, [hidden], [aria-hidden='true']";
const COPY_DISABLED_SELECTOR = "[data-llm-copy-disable-page]";
const LLM_COPY_ORIGIN = "https://jeongcheogi.edugamja.com";

function useCopyFeedback() {
  const [copyState, setCopyState] = useState<CopyState>("idle");
  const timerRef = useRef<number | null>(null);

  const mark = useCallback((state: CopyState) => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
    }

    setCopyState(state);
    timerRef.current = window.setTimeout(() => {
      setCopyState("idle");
      timerRef.current = null;
    }, 1800);
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, []);

  return [copyState, mark] as const;
}

function isPageCopyDisabled(): boolean {
  return document.querySelector(COPY_DISABLED_SELECTOR) !== null;
}

function usePageCopyDisabled() {
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    const update = () => setDisabled(isPageCopyDisabled());
    update();

    const observer = new MutationObserver(update);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-llm-copy-disable-page"],
    });

    return () => observer.disconnect();
  }, []);

  return disabled;
}

async function writeTextToClipboard(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.top = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();

  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);

  if (!copied) {
    throw new Error("Clipboard fallback failed");
  }
}

function normalizeInlineText(text: string): string {
  return text.replace(/\s+/g, " ");
}

function cleanupBlock(text: string): string {
  return text
    .split("\n")
    .map((line) => line.replace(/[ \t]+$/g, ""))
    .join("\n")
    .trim();
}

function shouldSkipElement(element: Element): boolean {
  return element.matches(SKIP_SELECTOR);
}

function textFromElement(element: Element): string {
  const clone = element.cloneNode(true) as Element;
  clone.querySelectorAll(SKIP_SELECTOR).forEach((child) => child.remove());
  return normalizeInlineText(clone.textContent ?? "").trim();
}

function wrapInlineCode(text: string): string {
  const cleaned = text.replace(/\s+/g, " ").trim();
  if (!cleaned) return "";
  const fence = cleaned.includes("`") ? "``" : "`";
  return `${fence}${cleaned}${fence}`;
}

function hrefToCopyUrl(href: string): string {
  const trimmed = href.trim();
  if (!trimmed) return "";

  if (/^(mailto:|tel:|sms:|javascript:)/i.test(trimmed)) {
    return trimmed;
  }

  try {
    const basePath =
      typeof window !== "undefined" ? window.location.pathname : "/";
    return new URL(trimmed, `${LLM_COPY_ORIGIN}${basePath}`).toString();
  } catch {
    return trimmed.startsWith("/")
      ? `${LLM_COPY_ORIGIN}${trimmed}`
      : trimmed;
  }
}

function escapeTableCell(text: string): string {
  return text.replace(/\|/g, "\\|").replace(/\n+/g, "<br>").trim();
}

function codeFence(code: string, language?: string | null): string {
  const cleanedCode = code.replace(/\n+$/g, "");
  const longestTickRun = Math.max(
    0,
    ...Array.from(cleanedCode.matchAll(/`+/g), (match) => match[0].length)
  );
  const fence = "`".repeat(Math.max(3, longestTickRun + 1));
  const languageLabel =
    language && language !== "text" && language !== "txt" ? language : "";

  return `${fence}${languageLabel}\n${cleanedCode}\n${fence}`;
}

function serializeInlineNode(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return normalizeInlineText(node.textContent ?? "");
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return "";
  }

  const element = node as Element;
  if (shouldSkipElement(element)) return "";

  const tagName = element.tagName.toLowerCase();

  if (tagName === "br") return "\n";
  if (tagName === "strong" || tagName === "b") {
    const text = serializeInlineChildren(element).trim();
    return text ? `**${text}**` : "";
  }
  if (tagName === "em" || tagName === "i") {
    const text = serializeInlineChildren(element).trim();
    return text ? `*${text}*` : "";
  }
  if (tagName === "code") {
    return wrapInlineCode(textFromElement(element));
  }
  if (tagName === "a") {
    const text =
      serializeInlineChildren(element).trim() || textFromElement(element);
    const href = element.getAttribute("href");
    return href && text ? `[${text}](${hrefToCopyUrl(href)})` : text;
  }
  if (tagName === "img") {
    return imageToMarkdown(element as HTMLImageElement);
  }

  return serializeInlineChildren(element);
}

function serializeInlineChildren(element: Element): string {
  return Array.from(element.childNodes).map(serializeInlineNode).join("");
}

function imageToMarkdown(image: HTMLImageElement): string {
  const alt = image.getAttribute("alt") ?? "";
  return alt;
}

function figureToMarkdown(element: Element): string {
  const image = element.querySelector("img");
  const explicitAlt = element.getAttribute("data-llm-image-alt");
  const explicitCaption = element.getAttribute("data-llm-image-caption");

  const alt = explicitAlt ?? image?.getAttribute("alt") ?? "";
  const caption =
    explicitCaption ?? element.querySelector("figcaption")?.textContent?.trim();

  return caption || alt;
}

function tableToMarkdown(table: Element): string {
  const rows = Array.from(table.querySelectorAll("tr"))
    .map((row) =>
      Array.from(row.querySelectorAll("th, td")).map((cell) =>
        escapeTableCell(textFromElement(cell))
      )
    )
    .filter((cells) => cells.length > 0);

  if (rows.length === 0) return "";

  const columnCount = Math.max(...rows.map((cells) => cells.length));
  const normalizeRow = (cells: string[]) =>
    Array.from({ length: columnCount }, (_, index) => cells[index] ?? "");
  const header = normalizeRow(rows[0]);
  const separator = Array.from({ length: columnCount }, () => "---");
  const bodyRows = rows.slice(1).map(normalizeRow);

  return [header, separator, ...bodyRows]
    .map((cells) => `| ${cells.join(" | ")} |`)
    .join("\n");
}

function listToMarkdown(list: Element, ordered: boolean): string {
  let index = Number.parseInt(list.getAttribute("start") ?? "1", 10);
  if (Number.isNaN(index)) index = 1;

  return Array.from(list.children)
    .filter((child) => child.tagName.toLowerCase() === "li")
    .map((item) => {
      const marker = ordered ? `${index++}.` : "-";
      const text = listItemToMarkdown(item).replace(
        /\n/g,
        `\n${" ".repeat(marker.length + 1)}`
      );
      return `${marker} ${text}`;
    })
    .join("\n");
}

function listItemToMarkdown(item: Element): string {
  const nestedLists: Element[] = [];
  const inlineNodes = Array.from(item.childNodes).filter((node) => {
    if (
      node.nodeType === Node.ELEMENT_NODE &&
      ["ul", "ol"].includes((node as Element).tagName.toLowerCase())
    ) {
      nestedLists.push(node as Element);
      return false;
    }

    return true;
  });

  const inlineText = inlineNodes
    .map((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        return normalizeInlineText(node.textContent ?? "");
      }

      if (node.nodeType !== Node.ELEMENT_NODE) {
        return "";
      }

      return serializeInlineNode(node);
    })
    .join("")
    .replace(/\s*\n\s*/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();

  const nestedText = nestedLists.map(serializeElement).filter(Boolean).join("\n");

  return [inlineText, nestedText].filter(Boolean).join("\n");
}

function blockquoteToMarkdown(element: Element): string {
  const content = serializeChildBlocks(element);
  return content
    .split("\n")
    .map((line) => (line ? `> ${line}` : ">"))
    .join("\n");
}

function detailsToMarkdown(element: Element): string {
  const summary = element.querySelector(":scope > summary");
  const summaryText = summary ? textFromElement(summary) : "접기/펼치기";
  const bodyNodes = Array.from(element.childNodes).filter(
    (node) => node !== summary
  );
  const body = serializeNodes(bodyNodes);

  return cleanupBlock(`**${summaryText}**\n\n${body}`);
}

function serializeCodeBlock(element: Element): string {
  const language = element.getAttribute("data-llm-code-language");
  const code =
    element.getAttribute("data-llm-code-text") ??
    element.querySelector("pre")?.textContent ??
    element.textContent ??
    "";

  return codeFence(code, language);
}

function serializeElement(element: Element): string {
  if (shouldSkipElement(element)) return "";

  const markdown = element.getAttribute("data-llm-markdown");
  if (markdown) {
    return cleanupBlock(markdown);
  }

  if (element.hasAttribute("data-llm-code-block")) {
    return serializeCodeBlock(element);
  }

  if (element.hasAttribute("data-llm-mermaid-block")) {
    const chart = element.getAttribute("data-llm-code-text") ?? "";
    return chart ? codeFence(chart, "mermaid") : "";
  }

  const tagName = element.tagName.toLowerCase();

  if (/^h[1-6]$/.test(tagName)) {
    const level = Number(tagName.slice(1));
    const text = textFromElement(element);
    return text ? `${"#".repeat(level)} ${text}` : "";
  }

  if (tagName === "p") {
    return cleanupBlock(serializeInlineChildren(element));
  }
  if (tagName === "pre") {
    const language = element
      .querySelector("code")
      ?.className.match(/language-(\w+)/)?.[1];
    return codeFence(element.textContent ?? "", language);
  }
  if (tagName === "blockquote") {
    return blockquoteToMarkdown(element);
  }
  if (tagName === "ul" || tagName === "ol") {
    return listToMarkdown(element, tagName === "ol");
  }
  if (tagName === "table") {
    return tableToMarkdown(element);
  }
  if (tagName === "figure") {
    return figureToMarkdown(element);
  }
  if (tagName === "img") {
    return imageToMarkdown(element as HTMLImageElement);
  }
  if (tagName === "details") {
    return detailsToMarkdown(element);
  }
  if (tagName === "hr") {
    return "---";
  }

  const inlineTextTags = new Set([
    "a",
    "span",
    "strong",
    "b",
    "em",
    "i",
    "code",
    "small",
  ]);

  if (inlineTextTags.has(tagName)) {
    return cleanupBlock(serializeInlineNode(element));
  }

  return serializeChildBlocks(element);
}

function serializeNode(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return cleanupBlock(normalizeInlineText(node.textContent ?? ""));
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return "";
  }

  return cleanupBlock(serializeElement(node as Element));
}

function headingLevelFromElement(element: Element): number | null {
  const match = element.tagName.toLowerCase().match(/^h([1-6])$/);
  return match ? Number(match[1]) : null;
}

function headingHasSerializableContent(
  nodes: Node[],
  headingIndex: number,
  level: number
): boolean {
  for (let index = headingIndex + 1; index < nodes.length; index += 1) {
    const node = nodes[index];

    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      const nextHeadingLevel = headingLevelFromElement(element);

      if (nextHeadingLevel !== null) {
        if (nextHeadingLevel <= level) {
          break;
        }

        if (headingHasSerializableContent(nodes, index, nextHeadingLevel)) {
          return true;
        }

        continue;
      }

      if (shouldSkipElement(element)) {
        continue;
      }
    }

    if (serializeNode(node)) {
      return true;
    }
  }

  return false;
}

function serializeNodes(nodes: Node[]): string {
  return nodes
    .filter((node, index) => {
      if (node.nodeType !== Node.ELEMENT_NODE) {
        return true;
      }

      const level = headingLevelFromElement(node as Element);
      if (level === null) {
        return true;
      }

      return headingHasSerializableContent(nodes, index, level);
    })
    .map(serializeNode)
    .map(cleanupBlock)
    .filter(Boolean)
    .join("\n\n")
    .trim();
}

function serializeChildBlocks(element: Element): string {
  return serializeNodes(Array.from(element.childNodes));
}

function collectSectionNodes(heading: HTMLElement): Node[] {
  const nodes: Node[] = [heading];
  let sibling = heading.nextSibling;

  while (sibling) {
    if (
      sibling.nodeType === Node.ELEMENT_NODE &&
      (sibling as Element).tagName.toLowerCase() === "h2"
    ) {
      break;
    }

    nodes.push(sibling);
    sibling = sibling.nextSibling;
  }

  return nodes;
}

async function copySerializedNodes(
  nodes: Node[],
  markCopyState: (state: CopyState) => void
) {
  const markdown = serializeNodes(nodes);
  if (!markdown) {
    markCopyState("error");
    return;
  }

  try {
    await writeTextToClipboard(markdown);
    markCopyState("copied");
  } catch (error) {
    console.error("LLM용 복사 실패:", error);
    markCopyState("error");
  }
}

function CopyStateIcon({ copyState }: { copyState: CopyState }) {
  if (copyState === "copied") {
    return <Check className="size-3.5" aria-hidden />;
  }

  return <Copy className="size-3.5" aria-hidden />;
}

export function MdxCopyRoot({ children }: { children: ReactNode }) {
  return (
    <div data-llm-copy-root>
      {children}
    </div>
  );
}

export function MdxPageCopyButton() {
  const copyDisabled = usePageCopyDisabled();
  const [copyState, markCopyState] = useCopyFeedback();

  const handleCopyPage = async () => {
    const root =
      document.querySelector<HTMLDivElement>("[data-llm-copy-root]");
    if (!root) return;
    await copySerializedNodes(Array.from(root.childNodes), markCopyState);
  };

  if (copyDisabled) {
    return null;
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={handleCopyPage}
      className={cn(
        "h-8 shrink-0 border-border bg-background text-xs font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground",
        copyState === "copied" &&
          "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300 dark:hover:bg-emerald-950/50",
        copyState === "error" &&
          "border-red-300 bg-red-50 text-red-700 hover:bg-red-50 dark:border-red-800 dark:bg-red-950/50 dark:text-red-300 dark:hover:bg-red-950/50"
      )}
      data-llm-copy-control="true"
      aria-label="페이지 전체를 LLM용 마크다운으로 복사"
    >
      <CopyStateIcon copyState={copyState} />
      <span aria-live="polite" aria-atomic="true">
        {copyState === "copied"
          ? "복사됨"
          : copyState === "error"
            ? "복사 실패"
            : "전체 LLM용 복사"}
      </span>
    </Button>
  );
}

export function MdxSectionCopyButton({
  sectionTitle,
}: {
  sectionTitle: string;
}) {
  const copyDisabled = usePageCopyDisabled();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [hasCopyableContent, setHasCopyableContent] = useState(false);
  const [copyState, markCopyState] = useCopyFeedback();

  useEffect(() => {
    const anchor = anchorRef.current;
    const heading = anchor?.closest("h2");
    if (!(heading instanceof HTMLElement)) return;

    const updateVisibility = () => {
      const hasContent = Boolean(
        serializeNodes(collectSectionNodes(heading)).trim()
      );
      setHasCopyableContent((current) =>
        current === hasContent ? current : hasContent
      );
    };

    updateVisibility();

    const observer = new MutationObserver(updateVisibility);
    observer.observe(heading.parentElement ?? heading, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => observer.disconnect();
  }, []);

  const handleCopySection = async (event: MouseEvent<HTMLButtonElement>) => {
    const heading = event.currentTarget.closest("h2");
    if (!(heading instanceof HTMLElement)) {
      markCopyState("error");
      return;
    }

    await copySerializedNodes(collectSectionNodes(heading), markCopyState);
  };

  return (
    <span
      ref={anchorRef}
      className="ml-auto inline-flex"
      data-llm-copy-control="true"
      hidden={copyDisabled || !hasCopyableContent}
    >
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleCopySection}
        className={cn(
          "h-7 px-2 text-xs font-medium text-muted-foreground hover:text-foreground",
          copyState === "copied" &&
            "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300 dark:hover:bg-emerald-950/50",
          copyState === "error" &&
            "border-red-300 bg-red-50 text-red-700 hover:bg-red-50 dark:border-red-800 dark:bg-red-950/50 dark:text-red-300 dark:hover:bg-red-950/50"
        )}
        aria-label={`${sectionTitle || "이 섹션"}을 LLM용 마크다운으로 복사`}
      >
        <CopyStateIcon copyState={copyState} />
        <span className="hidden sm:inline" aria-live="polite" aria-atomic="true">
          {copyState === "copied"
            ? "복사됨"
            : copyState === "error"
              ? "실패"
              : "LLM용 복사"}
        </span>
        <span className="sm:hidden" aria-live="polite" aria-atomic="true">
          {copyState === "copied"
            ? "완료"
            : copyState === "error"
              ? "실패"
              : "복사"}
        </span>
      </Button>
    </span>
  );
}
